import { useEffect, useRef, useState } from 'react';
import styles from './TopBar.module.css';
import { Popover } from './ui/Popover';
import { Menu } from './ui/Menu';
import { Calendar } from './ui/Calendar';
import { ProfileSelector } from './ProfileSelector';
import type { Profile } from '@/lib/auth';
import { captionFor, fmtShort, type DateRange } from '@/lib/dateRange';
import arrowDown16 from '@/assets/icons/arrow-down-16.svg';
import arrowDown12 from '@/assets/icons/arrow-down-12.svg';
import calendarIcon from '@/assets/icons/calendar-day.svg';
import settingsIcon from '@/assets/icons/settings.svg';
import userIcon from '@/assets/icons/user.svg';

export interface TopBarProps {
  title: string;
  /** Points per sparkline / trend granularity. Hidden when absent. */
  trendDays?: number;
  onTrendDaysChange?: (days: number) => void;
  range: DateRange;
  onRangeChange: (range: DateRange) => void;
  /** Resolved window echoed back by the API, so the bar shows real dates. */
  resolved?: {
    start?: string;
    end?: string;
    previous_start?: string;
    previous_end?: string;
    /**
     * False when the previous period falls outside what the page's source
     * holds. The bar must not advertise a comparison range the page is not
     * using — the dates would look like a comparison that simply came back
     * flat.
     */
    comparison_available?: boolean;
  };
  /** Latest day with data — the calendar will not offer anything later. */
  maxDate?: string;
  onSettingsClick?: () => void;
  userLabel?: string;
  onSignOut?: () => void;
  /** Brands this sign-in manages. Empty renders the pill disabled, not hidden. */
  profiles?: Profile[];
  activeProfile?: string | null;
  onProfileChange?: (name: string) => void;
}

const TREND_OPTIONS = [
  { value: 7, label: '7 Days' },
  { value: 14, label: '14 Days' },
  { value: 30, label: '30 Days' },
  { value: 60, label: '60 Days' },
  { value: 90, label: '90 Days' },
];

export function TopBar({
  title,
  trendDays,
  onTrendDaysChange,
  range,
  onRangeChange,
  resolved,
  maxDate,
  onSettingsClick,
  userLabel,
  onSignOut,
  profiles,
  activeProfile,
  onProfileChange,
}: TopBarProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent): void => {
      if (!menuRef.current?.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => { document.removeEventListener('mousedown', close); };
  }, [menuOpen]);

  return (
    <header className={styles.topbar}>
      <h1 className={styles.title}>{title}</h1>

      <div className={styles.controls}>
        {trendDays !== undefined && onTrendDaysChange && (
        <Popover
          align="right"
          trigger={({ toggle }) => (
            <button type="button" className={styles.trend} onClick={toggle} aria-haspopup="menu">
              <span className={styles.trendLabel}>Trend Chart:</span>
              <span className={styles.trendValue}>
                {TREND_OPTIONS.find((o) => o.value === trendDays)?.label ?? `${String(trendDays)} Days`}
                <img className={styles.icon16} src={arrowDown16} alt="" />
              </span>
            </button>
          )}
        >
          {({ close }) => (
            <Menu
              options={TREND_OPTIONS}
              value={trendDays}
              onSelect={(v) => { onTrendDaysChange(v); close(); }}
            />
          )}
        </Popover>
        )}

        <Popover
          align="right"
          trigger={({ toggle }) => (
            <button type="button" className={styles.dateRange} onClick={toggle} aria-haspopup="dialog">
              <img className={styles.calendar} src={calendarIcon} alt="" />
              <span className={styles.dateText}>
                <span className={styles.datePrimary}>
                  <strong>{captionFor(range)}</strong>{' '}
                  {resolved?.start ? `${fmtShort(resolved.start)} - ${fmtShort(resolved.end)}` : '—'}
                </span>
                <span className={styles.dateCompare}>
                  {range.compare_start && range.compare_end
                    ? `Compared: ${fmtShort(range.compare_start)} - ${fmtShort(range.compare_end)}`
                    : resolved?.comparison_available === false
                    ? 'No comparison — see the note below'
                    : `Compared: ${
                        resolved?.previous_start
                          ? `${fmtShort(resolved.previous_start)} - ${fmtShort(resolved.previous_end)}`
                          : '—'
                      }`}
                </span>
              </span>
              <img className={styles.icon16} src={arrowDown16} alt="" />
            </button>
          )}
        >
          {({ close }) => (
            <Calendar
              value={range}
              {...(maxDate ? { maxDate } : {})}
              onApply={(next) => { onRangeChange(next); close(); }}
              onCancel={close}
            />
          )}
        </Popover>

        {profiles && onProfileChange && (
          <ProfileSelector
            profiles={profiles}
            active={activeProfile ?? null}
            onSelect={onProfileChange}
          />
        )}

        <Popover
          align="right"
          trigger={({ toggle }) => (
            <button type="button" onClick={toggle} aria-label="Settings" aria-haspopup="menu">
              <img className={styles.icon20} src={settingsIcon} alt="" />
            </button>
          )}
        >
          {() => (
            <div className={styles.settings} role="menu">
              <p className={styles.settingsHead}>Data source</p>
              <p className={styles.settingsRow}>
                Window <strong>{resolved?.start ? `${resolved.start} → ${resolved.end ?? ''}` : '—'}</strong>
              </p>
              <p className={styles.settingsRow}>
                Compared <strong>{resolved?.previous_start ? `${resolved.previous_start} → ${resolved.previous_end ?? ''}` : '—'}</strong>
              </p>
              {trendDays !== undefined && (
                <p className={styles.settingsRow}>Trend points <strong>{trendDays}</strong></p>
              )}
              {onSettingsClick && (
                <button type="button" className={styles.settingsAction} onClick={onSettingsClick}>
                  Refresh data
                </button>
              )}
            </div>
          )}
        </Popover>

        <div className={styles.account} ref={menuRef}>
          <button
            type="button"
            className={styles.userBtn}
            onClick={() => { setMenuOpen((v) => !v); }}
            aria-label="Account"
            aria-expanded={menuOpen}
            aria-haspopup="menu"
          >
            <span className={styles.userStack}>
              <img className={styles.userIcon} src={userIcon} alt="" />
              <img className={styles.userCaret} src={arrowDown12} alt="" />
            </span>
          </button>

          {menuOpen && (
            <div className={styles.menu} role="menu">
              {userLabel && <p className={styles.menuUser}>{userLabel}</p>}
              <button
                type="button"
                role="menuitem"
                className={styles.menuItem}
                onClick={() => { setMenuOpen(false); onSignOut?.(); }}
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
